import {
  Component,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  OnInit,
} from '@angular/core';
import { SelectOption } from '../../../data/schemas/selectoption.schema';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-select',
  imports: [CommonModule],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  standalone: true,
})
export class SelectComponent implements ControlValueAccessor, OnInit {
  // ─── Inputs ──────────────────────────────────────────────────────────────

  label       = input<string>();
  options     = input.required<SelectOption[]>();
  placeholder = input<string>('Sélectionnez une option');
  loading     = input<boolean>(false);

  // ─── État interne ─────────────────────────────────────────────────────────

  public isOpen          = false;
  public filteredOptions: SelectOption[] = [];
  public displayValue    = '';
  public selectedValue: any = null;
  public isDisabled      = false;

  private _value: any    = null;
  private _isFocused     = false;

  // ─── CVA callbacks ────────────────────────────────────────────────────────

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void          = () => {};

  constructor(private elementRef: ElementRef) {
    // Réagit aux changements de la liste d'options (chargement async)
    effect(() => {
      this.filteredOptions = this.options();

      if (this._value !== null && this.options().length > 0) {
        this._updateDisplayValue(this._value);
      } else if (this._value === null) {
        this.displayValue = '';
      }
    });
  }

  ngOnInit(): void {
    this.filteredOptions = this.options();
  }

  // ─── ControlValueAccessor ─────────────────────────────────────────────────

  writeValue(value: any): void {
    this._value       = value;
    this.selectedValue = value;
    this._updateDisplayValue(value);
  }

  registerOnChange(fn: any): void  { this._onChange  = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // ─── Fermeture au clic extérieur ──────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isOpen) {
        this.isOpen = false;
        // Si l'utilisateur a tapé dans le champ sans sélectionner, restaurer la valeur
        this._updateDisplayValue(this._value);
        this._onTouched();
      }
    }
  }

  // ─── Événements input ─────────────────────────────────────────────────────

  onFocus(): void {
    if (!this.isDisabled && !this.loading()) {
      this._isFocused = true;
      this.isOpen     = true;
      this.filteredOptions = this.options();
    }
  }

  onBlurInput(): void {
    this._isFocused = false;
    // Délai pour laisser mousedown de l'option se déclencher avant blur
    setTimeout(() => {
      if (!this._isFocused) {
        this.isOpen = false;
        this._updateDisplayValue(this._value);
        this._onTouched();
      }
    }, 150);
  }

  // ─── Sélection d'une option ───────────────────────────────────────────────

  selectOption(option: SelectOption): void {
    this._value        = option.value;
    this.selectedValue = option.value;
    this.displayValue  = option.label;
    this.isOpen        = false;
    this.filteredOptions = this.options();

    this._onChange(this._value);
    this._onTouched();
  }

  // ─── Recherche / filtre ───────────────────────────────────────────────────

  onSearch(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.displayValue    = term;
    this.filteredOptions = term
      ? this.options().filter(o =>
          o.label.toLowerCase().includes(term.toLowerCase())
        )
      : this.options();
    this.isOpen = true;
    // Ne pas appeler _onChange : l'utilisateur filtre, il ne sélectionne pas encore
  }

  // ─── Utilitaire privé ─────────────────────────────────────────────────────

  private _updateDisplayValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.displayValue = '';
      return;
    }
    const found = this.options().find(o => o.value === value);
    this.displayValue = found ? found.label : '';
  }
}